import {
  CarrierHoverCell,
  EditOrderModal,
  GreenCheckIcon,
  Heading,
  InformationIcon,
  PencilIcon,
  PriceWithUnit,
  RedCheckIcon,
  SmartCarrierMatch,
  TrashIcon,
} from '@/components';
import {
  Info,
  Settings,
  Sparkles,
  User,
  Phone,
  MapPin,
  Package,
  Pencil,
  Trash2,
  CircleCheck,
  CircleX,
  ChevronDown,
  Check,
} from 'lucide-react';
import {
  COLUMN_CUSTOM_DETAILS,
  COLUMN_CUSTOMER_ADDRESS,
  COLUMN_IMPORT_VALID,
  COLUMN_ORDER_PAYMENT,
  COLUMN_PACKAGE_SIZE,
  COLUMN_PRODUCT_DETAILS,
  COLUMN_SHIPMENT,
  CUSTOM_ID,
  LOCAL_EN,
  ORDER_ACTIONS,
  ORDER_ID,
  STYLE_SHARE_TAG,
  THEME_VARIABLE,
} from '@/config';
import {
  ButtonFilter,
  Carrier,
  ImportItem,
  OrderDataImport,
  OrderExcelResponse,
  PaymentType,
  ShipmentFeeType,
} from '@/types';
import { Button, Divider, Flex, message, Spin, Tooltip, Typography } from 'antd';
import { Popover, PopoverContent, PopoverTrigger, PopoverClose } from '@/components/ui/popover';
import { ColumnsType } from 'antd/es/table';
import { Key, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { useBulkImportStore, useConfirmModalStore } from '@/stores';
import { ConfirmTypeEnum } from '@/components';
import { ROUTE } from '@/config';
import {
  useDeleteOrderExcelById,
  useFetchOrderExcelByUID,
  useFetchOrderInValidExcelByUID,
  useFetchOrderNoCarrierExcelByUID,
  useFetchOrderValidExcelByUID,
  useRevalidateOrderExcel,
} from '@/hooksApi/useImport';
import { updateOrderExcelRow } from '@/api/importService';
import { useListShippingRules } from '@/hooksApi/useShippingRule';
import { isImportRowCarrierLocked } from '@/utils/isImportRowCarrierLocked';
import { PackageSizeEnum, PackageSizeFormValues } from '@/components/OrderForm/PackageSizeCard';
import { normalizeToTranslationKey } from '@/utils';
import { ImportRuleLabel } from '@/components/ShippingRules/ImportRuleLabel';

const { Text } = Typography;

const getCarrierInitials = (name: string | undefined | null) => {
  if (!name) return '?';
  const words = name.split(' ');
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const carrierColors: Record<string, string> = {
  'Al Saari': 'hsl(210,60%,50%)',
  'Hi Express': 'hsl(25,80%,50%)',
  Sandoog: 'hsl(145,50%,40%)',
  Waseet: 'hsl(260,50%,55%)',
  Zaeem: 'hsl(0,65%,50%)',
};

const getCarrierColor = (name: string) => carrierColors[name] || 'hsl(210,10%,50%)';

// Task 7: pick the reason to show in the carrier-column "no carrier" popover.
// The backend already writes a province-named bilingual message to
// `record.errors.temperature_control` when no temperature-control carrier
// serves the row's province — prefer that over the generic carrier error
// sources so the merchant sees *why* there's no carrier, not just that
// there isn't one. Returns undefined when no error info is present at all,
// letting the caller fall back to the generic i18n string.
export const getNoCarrierErrorMessage = (
  record: Pick<OrderDataImport, 'errors' | 'carrier_error_message'>,
): string | undefined => {
  const tempReason = record?.errors?.temperature_control;
  if (tempReason) return tempReason;

  if (record?.carrier_error_message) return record.carrier_error_message;

  if (record?.errors && typeof record.errors === 'object' && 'carrier' in record.errors) {
    return (record.errors as Record<string, string>).carrier;
  }

  return undefined;
};

const CarrierLogo = ({ logoUrl, title }: { logoUrl: string; title: string }) => {
  const [failed, setFailed] = useState(false);
  const displayTitle = title || '';

  if (!logoUrl || failed) {
    return (
      <div className='flex items-center gap-1.5'>
        <div
          className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white text-[10px] font-bold'
          style={{ backgroundColor: getCarrierColor(displayTitle) }}
        >
          {getCarrierInitials(displayTitle)}
        </div>
        <span className='text-xs font-medium text-sd-foreground whitespace-nowrap'>
          {displayTitle}
        </span>
      </div>
    );
  }

  return (
    <div className='flex items-center gap-1.5'>
      <div className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white overflow-hidden shadow-sm border border-gray-100'>
        <img
          src={logoUrl}
          alt={displayTitle}
          width={28}
          height={28}
          className='object-contain'
          onError={() => setFailed(true)}
        />
      </div>
      <span className='text-xs font-medium text-sd-foreground whitespace-nowrap'>
        {displayTitle}
      </span>
    </div>
  );
};

const userImportData = (
  id: string,
  current: any,
  buttonFilter: ButtonFilter,
  setCurrentPage: (page: number) => void,
  pageSize?: number,
  pickupAddressRegionUid?: string,
  selectedPickupAddressId?: string,
) => {
  const [editingOrderUid, setEditingOrderUid] = useState<string | undefined>(undefined);
  const [updatingCarrierRows, setUpdatingCarrierRows] = useState<Set<string>>(new Set());

  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  const [idSelect, setIdSelect] = useState<string | null>(null);
  const navigate = useNavigate();
  const { setPendingImport } = useBulkImportStore();
  const { openConfirm } = useConfirmModalStore();
  // Fetch All data
  const {
    data: dataOrderExcel,
    isLoading: loadingOrderExcel,
    isFetching: isFetchingOrderExcel,
  } = useFetchOrderExcelByUID(id, current, pageSize, buttonFilter === ButtonFilter.All);

  // Fetch valid data
  const {
    data: dataOrderValidExcel,
    isLoading: loadingOrderValidExcel,
    isFetching: isFetchingOrderValidExcel,
  } = useFetchOrderValidExcelByUID(id, current, pageSize, buttonFilter === ButtonFilter.VALID);

  // Fetch invalid data
  const {
    data: dataOrderInValidExcel,
    isLoading: loadingOrderInValidExcel,
    isFetching: isFetchingOrderInValidExcel,
  } = useFetchOrderInValidExcelByUID(id, current, pageSize, buttonFilter === ButtonFilter.INVALID);

  // Fetch no carrier data (server-side)
  const {
    data: dataOrderNoCarrierExcel,
    isLoading: loadingOrderNoCarrierExcel,
    isFetching: isFetchingOrderNoCarrierExcel,
  } = useFetchOrderNoCarrierExcelByUID(
    id,
    current,
    pageSize,
    buttonFilter === ButtonFilter.NO_CARRIER,
  );

  // Always-enabled count query for no-carrier tab badge (perpage=1, we only need the total)
  const { data: dataOrderNoCarrierCount } = useFetchOrderNoCarrierExcelByUID(id, 1, 1, true);

  // Revalidate carrier matching
  const { mutate: revalidateExcel, isLoading: isRevalidating } = useRevalidateOrderExcel();

  // SRE-3 — map of rule uid -> is_overridable so the carrier column can lock
  // (read-only text) rows whose driving rule is NOT overridable. Covers both
  // agent and merchant rules (the list endpoint returns all of them).
  const { data: shippingRulesData } = useListShippingRules();
  const rulesOverridableByUid = useMemo(() => {
    const map = new Map<string, boolean>();
    (shippingRulesData?.object ?? []).forEach((r) => {
      if (r?.uid) map.set(r.uid, r.is_overridable === true);
    });
    return map;
  }, [shippingRulesData]);

  const handleRevalidate = (onComplete?: () => void) => {
    revalidateExcel(
      { uid: id, savedPickUpAddressUid: selectedPickupAddressId },
      {
        onSuccess: () => {
          message.success(t('revalidation_complete'));
          queryClient.invalidateQueries(['import', id]);
          queryClient.invalidateQueries(['import-valid', id]);
          queryClient.invalidateQueries(['import-invalid', id]);
          queryClient.invalidateQueries(['import-no-carrier', id]);
          onComplete?.();
        },
        onError: () => {
          message.error(t('revalidation_failed'));
          onComplete?.();
        },
      },
    );
  };

  // Delete order excel
  const { mutate: deleteOrderExcelById, isLoading: loadingDeleteOrder } = useDeleteOrderExcelById();

  const executeDelete = (key: string) => {
    deleteOrderExcelById(
      {
        key,
        uid: id,
      },
      {
        onSuccess: async () => {
          message.success(t('operation'));

          queryClient.invalidateQueries(['import', id]);
          queryClient.invalidateQueries(['import-valid', id]);
          queryClient.invalidateQueries(['import-invalid', id]);
          queryClient.invalidateQueries(['import-no-carrier', id]);

          if (isTheOnlyItem()) {
            setCurrentPage(current - 1);
          }
        },
      },
    );
  };

  const handleDelete = (key: string) => {
    openConfirm({
      title: t('permission.delete_imported_order'),
      description: t('permission.delete_imported_order_description'),
      approveText: t('delete'),
      cancelText: t('cancel'),
      confirmType: ConfirmTypeEnum.DESTRUCTIVE,
      onConfirm: () => executeDelete(key),
      onCancel: () => setIdSelect(null),
    });
  };

  const isTheOnlyItem = () => {
    const tableItemList = getCurrentDataTable();

    return tableItemList.length === 1;
  };

  const getCurrentDataTable = () => {
    if (buttonFilter === ButtonFilter.NO_CARRIER) {
      return mapOrderData(dataOrderNoCarrierExcel?.object?.items || []);
    }

    return buttonFilter === ButtonFilter.All
      ? mapOrderData(dataOrderExcel?.object?.items || [])
      : buttonFilter === ButtonFilter.VALID
        ? mapOrderData(dataOrderValidExcel?.object?.items || [])
        : mapOrderData(dataOrderInValidExcel?.object?.items || []);
  };
  const handleCarrierSelect = async (rowUid: string, carrierSlug: string) => {
    if (!rowUid || updatingCarrierRows.has(rowUid)) return;
    setUpdatingCarrierRows((prev) => new Set(prev).add(rowUid));
    try {
      await updateOrderExcelRow(id, rowUid, { selected_carrier_slug: carrierSlug });
      queryClient.invalidateQueries(['import', id]);
      queryClient.invalidateQueries(['import-valid', id]);
      queryClient.invalidateQueries(['import-invalid', id]);
      queryClient.invalidateQueries(['import-no-carrier', id]);
      message.success(String(t('carrier_updated')));
    } catch (e) {
      message.error(String(t('carrier_update_failed')));
    } finally {
      setUpdatingCarrierRows((prev) => {
        const next = new Set(prev);
        next.delete(rowUid);
        return next;
      });
    }
  };

  const columns: ColumnsType<OrderDataImport> = [
    {
      title: t('validity'),
      dataIndex: COLUMN_IMPORT_VALID,
      key: COLUMN_IMPORT_VALID,
      render: (validity: string, record: OrderDataImport) => {
        const keysErrors = record?.errors && Object.keys(record?.errors);

        return validity === 'VALID' ? (
          <div className='flex justify-center'>
            <CircleCheck className='h-5 w-5 text-[hsl(145,55%,40%)]' strokeWidth={2} />
          </div>
        ) : (
          <Tooltip
            title={
              keysErrors &&
              keysErrors?.length > 0 && (
                <Flex vertical>
                  <Text>
                    {keysErrors?.map((key: string, index: number) => (
                      <span key={index}>
                        {key}
                        <br />
                      </span>
                    ))}
                  </Text>
                  <Divider />
                  {keysErrors?.map((error: string, index: Key | undefined) => (
                    <Text key={index}>{`. ${record?.errors[error]}`}</Text>
                  ))}
                </Flex>
              )
            }
            placement='right'
            color='#fff'
            overlayInnerStyle={{ color: THEME_VARIABLE.customTunaPrimary }}
          >
            <div className='flex justify-center'>
              <CircleX className='h-5 w-5 text-[#dc2828]' strokeWidth={2} />
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: t('carrier'),
      dataIndex: 'carrier',
      key: 'carrier',
      render: (_carrier: Carrier, record: OrderDataImport) => {
        const availableCarriers = record.availableCarriersParsed || [];

        // SRE-3 — when a NON-overridable rule drove this row's carrier, the
        // merchant cannot change it: show the carrier as read-only text (no
        // dropdown). Overridable rules / no-rule rows keep the dropdown.
        const carrierLocked = isImportRowCarrierLocked(
          record.recommended_rule_uid,
          rulesOverridableByUid,
        );

        // Determine the active carrier slug: prefer explicit selection, fall back to recommended
        const activeSlug = record.selected_carrier_slug || record.recommended_carrier_slug || null;

        // Find the active carrier's display data from available_carriers_detail
        const activeCarrierDetail = availableCarriers.find((c) => c.carrier_slug === activeSlug);

        // If no available carriers at all, show the warning popover (no-carrier state)
        if (availableCarriers.length === 0) {
          const carrierMessage =
            getNoCarrierErrorMessage(record) || String(t('no_active_carrier_available'));

          const noCarrierPopoverContent = (
            <div className='bg-emerald-50 px-3 py-3 rounded-xl'>
              <div className='flex items-center gap-1.5 mb-1.5'>
                <Sparkles className='h-3.5 w-3.5 text-emerald-600' strokeWidth={1.5} />
                <span className='text-xs font-semibold text-emerald-700'>
                  {String(t('recommended'))}
                </span>
              </div>
              <div className='mb-2.5'>
                <span className='text-xs text-foreground'>{carrierMessage}</span>
              </div>
              <Link
                to={ROUTE.SETTINGS_SHIPPING_RULES}
                onClick={() => {
                  setPendingImport(id, 2);
                }}
                className='inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 hover:text-emerald-900 transition-colors no-underline'
              >
                <Settings className='h-3 w-3' strokeWidth={1.5} />
                {String(t('carrier_settings'))}
              </Link>
            </div>
          );

          return (
            <div className='sd-scope sd-font'>
              <Popover>
                <PopoverTrigger asChild>
                  <button className='flex items-center justify-center rounded-full w-7 h-7 hover:bg-muted transition-colors border-none bg-transparent cursor-pointer p-0'>
                    <Info className='h-4.5 w-4.5 text-amber-500' strokeWidth={2} />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className='sd-scope sd-font w-[220px] !p-0 rounded-xl !shadow-lg !border-gray-200 !bg-white'
                  align={isRtl ? 'end' : 'start'}
                >
                  {noCarrierPopoverContent}
                </PopoverContent>
              </Popover>
            </div>
          );
        }

        // Build trigger display: use activeCarrierDetail if available, otherwise derive from slug
        const triggerLogoUrl: string | null = activeCarrierDetail?.logo_url?.abs_url || null;
        let triggerTitle: string = activeCarrierDetail?.carrier_name
          ? String(t(normalizeToTranslationKey(activeCarrierDetail.carrier_name)))
          : '';
        if (!triggerTitle && activeSlug) {
          const slugDerived = activeSlug
            .split('-')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
          triggerTitle = String(t(normalizeToTranslationKey(slugDerived)));
        }

        const selectorPopoverContent = (
          <div className='py-1'>
            {availableCarriers.map((c) => {
              const isSelected = c.carrier_slug === activeSlug;
              return (
                <PopoverClose asChild key={c.carrier_slug}>
                  <button
                    onClick={() => handleCarrierSelect(record.key, c.carrier_slug)}
                    className='w-full flex items-center gap-2.5 px-3 py-2 hover:bg-muted transition-colors border-none bg-transparent cursor-pointer text-left'
                  >
                    <div className='flex-1 flex items-center gap-2'>
                      <CarrierLogo
                        logoUrl={c.logo_url?.abs_url || ''}
                        title={String(t(normalizeToTranslationKey(c.carrier_name)))}
                      />
                    </div>
                    <div className='flex items-center gap-2 shrink-0'>
                      <span className='text-[11px] text-muted-foreground whitespace-nowrap'>
                        {c.shipping_fee != null && isFinite(c.shipping_fee)
                          ? Math.round(c.shipping_fee).toLocaleString()
                          : '—'}{' '}
                        {String(t('iqd'))}
                      </span>
                      {isSelected && (
                        <Check
                          className='h-3.5 w-3.5 text-emerald-600 shrink-0'
                          strokeWidth={2.5}
                        />
                      )}
                    </div>
                  </button>
                </PopoverClose>
              );
            })}
          </div>
        );

        // Shared inner display of the active carrier (logo + title, or a
        // "select carrier" placeholder). Reused by both the locked (text) and
        // editable (dropdown) renders so they look identical bar the chevron.
        const carrierDisplay =
          activeSlug && triggerTitle ? (
            triggerLogoUrl ? (
              <CarrierLogo logoUrl={triggerLogoUrl} title={triggerTitle} />
            ) : (
              <div className='flex items-center gap-1.5'>
                <div
                  className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white text-[10px] font-bold'
                  style={{ backgroundColor: getCarrierColor(triggerTitle) }}
                >
                  {getCarrierInitials(triggerTitle)}
                </div>
                <span className='text-xs font-medium text-sd-foreground whitespace-nowrap'>
                  {triggerTitle}
                </span>
              </div>
            )
          ) : (
            <span className='text-xs text-muted-foreground'>
              {String(t('select_carrier'))}
            </span>
          );

        // SRE-3 — locked (non-overridable rule): read-only text, no dropdown.
        if (carrierLocked) {
          return (
            <div className='sd-scope sd-font flex flex-col items-start'>
              <div
                className='flex items-center gap-1 px-1.5 py-1'
                data-testid='import-carrier-locked'
              >
                {carrierDisplay}
              </div>
              <ImportRuleLabel ruleName={record.recommended_rule_name} t={t} />
            </div>
          );
        }

        return (
          <div className='sd-scope sd-font flex flex-col items-start'>
            <Popover>
              <PopoverTrigger asChild>
                <button className='flex items-center gap-1 rounded-md px-1.5 py-1 hover:bg-muted transition-colors border-none bg-transparent cursor-pointer p-0 group'>
                  {carrierDisplay}
                  <ChevronDown
                    className='h-3 w-3 text-muted-foreground shrink-0 opacity-60 group-hover:opacity-100 transition-opacity'
                    strokeWidth={2}
                  />
                </button>
              </PopoverTrigger>
              <PopoverContent
                className='sd-scope sd-font w-[280px] !p-0 rounded-xl !shadow-lg !border-gray-200 !bg-white'
                align={isRtl ? 'end' : 'start'}
              >
                {selectorPopoverContent}
              </PopoverContent>
            </Popover>
            {/* Item 2 — show the shipping rule that produced this recommended
                carrier. Renders nothing when no rule matched the row. */}
            <ImportRuleLabel ruleName={record.recommended_rule_name} t={t} />
          </div>
        );
      },
    },
    {
      title: t('custom_id'),
      dataIndex: ORDER_ID,
      key: ORDER_ID,
      render: (text) => <Heading title={text} />,
    },
    {
      title: t('customer_details'),
      key: COLUMN_CUSTOM_DETAILS,
      render: (_, record: OrderDataImport) => (
        <Flex vertical gap={2}>
          <Flex align='center' gap={6}>
            <User size={14} className='text-gray-400 shrink-0' strokeWidth={1.5} />
            <span className='text-sm font-medium text-sd-foreground'>{record.customerName}</span>
          </Flex>
          <Flex align='center' gap={6}>
            <Phone size={12} className='text-[#737b8c] shrink-0' strokeWidth={1.5} />
            <span className='text-xs text-[#737b8c]'>{record.customerPhone}</span>
          </Flex>
        </Flex>
      ),
    },
    {
      title: t('customer_address'),
      dataIndex: COLUMN_CUSTOMER_ADDRESS,
      key: COLUMN_CUSTOMER_ADDRESS,
      render: (text) => (
        <Flex align='center' gap={6}>
          <MapPin size={14} className='text-red-500 shrink-0' strokeWidth={1.5} />
          <span className='text-sm text-gray-600'>{text}</span>
        </Flex>
      ),
    },
    {
      title: t('product_details'),
      key: COLUMN_PRODUCT_DETAILS,
      render: (record: OrderDataImport) => (
        <Flex align='center' gap={6}>
          <Package size={14} className='text-orange-500 shrink-0' strokeWidth={1.5} />
          <span className='text-sm text-sd-foreground whitespace-nowrap'>
            {record?.quantity ?? 0} {t('items')} ·{' '}
            {typeof record?.total === 'number' ? record.total.toLocaleString() : record?.total || 0}{' '}
            {t('iqd')}
          </span>
        </Flex>
      ),
    },
    {
      title: t('package_size'),
      dataIndex: COLUMN_PACKAGE_SIZE,
      key: COLUMN_PACKAGE_SIZE,
      render: (text) => (
        <span className='inline-flex items-center rounded-full bg-[#f1f0ee] px-2.5 py-0.5 text-xs font-medium text-sd-foreground capitalize'>
          {t(PackageSizeEnum[text as PackageSizeFormValues])}
        </span>
      ),
    },
    {
      title: t('order_payment'),
      dataIndex: COLUMN_ORDER_PAYMENT,
      key: COLUMN_ORDER_PAYMENT,
      render: (text) => {
        const label = text === PaymentType.COLLECT_ON_DELIVERY ? 'COD' : t('prepaid');
        return <span className='text-sm text-sd-foreground whitespace-nowrap'>{label}</span>;
      },
    },
    {
      title: t('shipment_payment'),
      dataIndex: COLUMN_SHIPMENT,
      key: COLUMN_SHIPMENT,
      render: (text) => {
        const label = text === ShipmentFeeType.BY_CUSTOMER ? t('customer') : t('merchant');
        return <span className='text-sm text-sd-foreground whitespace-nowrap'>{label}</span>;
      },
    },
    {
      title: t('actions'),
      key: ORDER_ACTIONS,
      fixed: isRtl ? ('left' as const) : ('right' as const),
      width: 88,
      render: (record: OrderDataImport) => (
        <Flex align='center' gap={8}>
          <Button
            onClick={() => setEditingOrderUid(record?.key)}
            className='import-action-btn-edit'
          >
            <Pencil size={16} strokeWidth={2} />
          </Button>
          <Button
            className='import-action-btn-delete'
            onClick={() => {
              handleDelete?.(record?.key);
              setIdSelect?.(record?.key);
            }}
          >
            {idSelect === record?.key ? (
              <Spin size='small' />
            ) : (
              <Trash2 size={14} strokeWidth={2} />
            )}
          </Button>
        </Flex>
      ),
    },
  ];

  const mapOrderData = (data: OrderExcelResponse['object']['items'] | undefined) => {
    return data && data.length > 0
      ? data.map((item: ImportItem) => {
          // Parse quantity: split by comma, sum values.
          // Also handle corrupted data where quantities were concatenated without commas
          // by comparing with title count.
          const titleCount =
            item.product_title?.split(',').filter((t: string) => t.trim()).length || 1;
          const rawQuantities =
            item.product_quantity
              ?.split(',')
              .map((q: string) => Number(q.trim()))
              .filter((n: number) => !isNaN(n) && n > 0) || [];
          const totalQuantity =
            rawQuantities.length > 0
              ? rawQuantities.reduce((acc: number, current: number) => acc + current, 0)
              : titleCount;

          return {
            key: item?.uid,
            validity: item?.validation_status || '',
            orderId: item?.custom_id || '',
            customerName: item?.full_name || '',
            customerPhone: item?.phone || '',
            customerAddress: item?.address_text || '',
            quantity: totalQuantity,
            total: item?.products_value || '',
            packageSize: item?.size || '',
            orderPayment: item?.payment_type || '',
            shipmentPayment: item?.shipment_fee_type || '',
            errors: (() => {
              if (!item?.errors) return {};
              if (typeof item.errors === 'object') return item.errors;
              try {
                return JSON.parse(item.errors);
              } catch {
                return {};
              }
            })(),
            carrierList: item?.carrierList ?? [],
            validation_status: item?.validation_status,
            recommended_carrier_slug: item?.recommended_carrier_slug,
            selected_carrier_slug: item?.selected_carrier_slug,
            available_carriers_detail: item?.available_carriers_detail,
            availableCarriersParsed: (() => {
              try {
                return JSON.parse(item?.available_carriers_detail || '[]');
              } catch {
                return [];
              }
            })(),
            has_carrier_error: item?.has_carrier_error,
            carrier_error_message: item?.carrier_error_message,
            // Item 2 — shipping-rule attribution for the recommended carrier.
            recommended_rule_name: item?.recommended_rule_name ?? null,
            recommended_rule_uid: item?.recommended_rule_uid ?? null,
          };
        })
      : [];
  };

  // Current view total (for pagination)
  const currentTotal =
    buttonFilter === ButtonFilter.NO_CARRIER
      ? dataOrderNoCarrierExcel?.object?.total || 0
      : buttonFilter === ButtonFilter.All
        ? dataOrderExcel?.object?.total || 0
        : buttonFilter === ButtonFilter.VALID
          ? dataOrderValidExcel?.object?.total || 0
          : dataOrderInValidExcel?.object?.total || 0;

  // Overall counts from ALL data (cached from initial load, persists across tab switches)
  // React Query keeps stale data even when the query is disabled
  const allTotal = dataOrderExcel?.object?.total || 0;
  const allInvalidRowsCount = dataOrderExcel?.object?.invalid_rows_count || 0;
  const allNoCarrierCount = dataOrderNoCarrierCount?.object?.total || 0;

  return {
    columns,
    editingOrderUid: editingOrderUid,
    setEditingOrderUid: setEditingOrderUid,
    dataTables: getCurrentDataTable(),
    loadingOrderExcel:
      buttonFilter === ButtonFilter.All
        ? loadingOrderExcel || isFetchingOrderExcel
        : buttonFilter === ButtonFilter.NO_CARRIER
          ? loadingOrderNoCarrierExcel || isFetchingOrderNoCarrierExcel
          : buttonFilter === ButtonFilter.VALID
            ? loadingOrderValidExcel || isFetchingOrderValidExcel
            : loadingOrderInValidExcel || isFetchingOrderInValidExcel,
    total: currentTotal,
    invalid_rows_count: allInvalidRowsCount,
    all_total: allTotal,
    all_invalid_rows_count: allInvalidRowsCount,
    all_no_carrier_count: allNoCarrierCount,
    handleRevalidate,
    isRevalidating,
  };
};

export default userImportData;
